import type { HTMLAttributes, ReactNode } from 'react';

import { clsx } from 'clsx';

import { cn } from '../../lib/utils';

export interface BoxProps {
    variant?: 'default' | 'plain' | 'compact';
    title?: ReactNode;
    subtitle?: ReactNode;
    action?: ReactNode;
    children: ReactNode;
    bordered?: boolean;
    dataTestid?: string;
    className?: string;
    htmlAttributes?: HTMLAttributes<HTMLDivElement>;
}

export const Box = ({
    variant = 'default',
    title,
    subtitle,
    action = null,
    children,
    dataTestid,
    bordered = false,
    className = '',
    htmlAttributes,
}: BoxProps) => (
    <div
        className={cn(
            `rounded-xl`,
            {
                'px-8 pb-6 pt-8 bg-white': variant === 'default',
                'px-4 pb-3 pt-4 bg-white': variant === 'compact',
                'border border-gray-200 border-solid': bordered && (variant === 'default' || variant === 'compact'),
            },
            className
        )}
        data-testid={dataTestid}
        data-component-name='Box'
        {...htmlAttributes}
    >
        {(title || action) && (
            <div className='flex justify-between'>
                {title && (
                    <div>
                        <h3
                            className={clsx('text-lg font-semibold', { 'mb-6': !subtitle })}
                            data-testid={dataTestid && `${dataTestid}-title`}
                            {...htmlAttributes}
                        >
                            {title}
                        </h3>
                        {subtitle && (
                            <h4
                                className='mb-6 text-sm text-gray-600'
                                data-testid={dataTestid && `${dataTestid}-subtitle`}
                            >
                                {subtitle}
                            </h4>
                        )}
                    </div>
                )}
                {action && <div className='mb-6'>{action}</div>}
            </div>
        )}

        {children}
    </div>
);
